import { FiClock, FiLogIn, FiLogOut } from "react-icons/fi";
import AttendanceStatusBadge from "./AttendanceStatusBadge";

const statusLabels = {
  PRESENT: "Present",
  LATE: "Late",
  ABSENT: "Absent",
  ON_LEAVE: "On Leave",
  HALF_DAY: "Half Day",
  WEEKLY_OFF: "Weekly Off",
  HOLIDAY: "Holiday",
};

function formatTime(dateValue) {
  if (!dateValue) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(dateValue));
}

export default function AttendanceTable({ records, isLoading }) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12">
        <div className="flex justify-center">
          <div className="h-9 w-9 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Employee
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Check In
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Check Out
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Shift
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {records.length ? (
              records.map((employee) => {
                const checkIn = formatTime(employee.checkInAt);
                const checkOut = formatTime(employee.checkOutAt);
                const displayStatus =
                  statusLabels[employee.status] || employee.status;

                const initials = employee.fullName
                  ?.split(" ")
                  .map((word) => word[0])
                  .join("")
                  .slice(0, 2);

                return (
                  <tr
                    key={employee.id}
                    className="group border-b border-gray-100 last:border-b-0 hover:bg-primary-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-700 to-primary-900 text-white flex items-center justify-center font-semibold group-hover:scale-105 transition-transform duration-300">
                          {initials || "EM"}
                        </div>

                        <div>
                          <p className="font-medium text-gray-900">
                            {employee.fullName}
                          </p>

                          <p className="text-sm text-gray-500">
                            {employee.employeeCode}
                            {employee.designation
                              ? ` · ${employee.designation}`
                              : ""}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="inline-flex items-center gap-2 text-sm">
                        <FiLogIn
                          size={14}
                          className={
                            employee.checkInAt
                              ? "text-emerald-500"
                              : "text-gray-300"
                          }
                        />
                        <span
                          className={
                            employee.checkInAt
                              ? "font-medium text-gray-900"
                              : "font-medium text-gray-400"
                          }
                        >
                          {checkIn}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="inline-flex items-center gap-2 text-sm">
                        <FiLogOut
                          size={14}
                          className={
                            employee.checkOutAt
                              ? "text-gray-500"
                              : "text-gray-300"
                          }
                        />
                        <span
                          className={
                            employee.checkOutAt
                              ? "font-medium text-gray-900"
                              : "font-medium text-gray-400"
                          }
                        >
                          {checkOut}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600">
                        General
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <AttendanceStatusBadge status={displayStatus} />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="py-16">
                  <div className="flex flex-col items-center gap-3 text-gray-400">
                    <div className="h-12 w-12 rounded-2xl bg-gray-50 flex items-center justify-center">
                      <FiClock size={22} />
                    </div>

                    <p className="text-gray-500">
                      No attendance records found.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}